import { Loader2 } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { StrongText } from "@/components/strong-text";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { type Team, useDeleteTeam } from "@/lib/api";
import { apiErrorMessage, inlineFailure } from "@/lib/api-errors";

interface TeamDeleteDialogProps {
  team: Team | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/** Asks before deleting a team; its members keep their other teams' access. */
export function TeamDeleteDialog({ team, open, onOpenChange }: TeamDeleteDialogProps) {
  const { t } = useTranslation();
  const deleteTeam = useDeleteTeam();
  // The failed attempt, worded at render so a language switch re-words it.
  const [failure, setFailure] = useState<{ error: unknown } | null>(null);

  const changeOpen = (next: boolean) => {
    setFailure(null);
    onOpenChange(next);
  };

  const handleDelete = async () => {
    if (!team) {
      return;
    }
    setFailure(null);
    try {
      await deleteTeam.mutateAsync({ teamId: team.id });
      onOpenChange(false);
    } catch (err) {
      setFailure(inlineFailure({ error: err }));
    }
  };
  const error = failure && apiErrorMessage(failure.error, "deleteTeam");

  return (
    <Dialog open={open} onOpenChange={changeOpen}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t("teams.deleteTitle")}</DialogTitle>
          <DialogDescription>
            <StrongText i18nKey="teams.deleteBody" values={{ team: team?.name ?? "" }} />
          </DialogDescription>
        </DialogHeader>
        {error && (
          <p role="alert" className="text-destructive text-sm">
            {error}
          </p>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => changeOpen(false)}>
            {t("common.cancel")}
          </Button>
          <Button
            variant="destructive"
            onClick={() => void handleDelete()}
            disabled={deleteTeam.isPending}
          >
            {deleteTeam.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              t("common.delete")
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
